const ADMIN_API_BASE = import.meta.env.PROD && import.meta.env.VITE_API_PROXY_TARGET
  ? `${import.meta.env.VITE_API_PROXY_TARGET}/api/admin`
  : '/api/admin';

async function fetchAdmin<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${ADMIN_API_BASE}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };

  const token = localStorage.getItem('auth_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: `HTTP error! status: ${response.status}` }));
    const message =
      (typeof error?.message === 'string' && error.message) ||
      (typeof error?.error === 'string' && error.error) ||
      (typeof error?.description === 'string' && error.description) ||
      `HTTP error! status: ${response.status}`;
    throw new Error(message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export interface AdminMeResponse {
  admin: boolean;
}

export interface AdminStats {
  totalFacilities: number;
  scrapeCoverage24h: number;
  errorRate24h: number;
  activeAlerts: number;
  notificationsSent24h: number;
  subscriptions: {
    pro: number;
    free: number;
  };
}

export interface PhaseBreakdownEntry {
  source: string;
  cnt: number;
  succeeded: number;
  failed: number;
}

export interface HourlyBucket {
  bucket: number;
  cnt: number;
}

export interface ScrapingSummary {
  phaseBreakdown: PhaseBreakdownEntry[];
  avgDurationMs24h: number;
  hourlyBuckets: HourlyBucket[];
}

export interface ScrapingError {
  id: number;
  facilityId: string | null;
  facilityName: string | null;
  errorMessage: string;
  source: string | null;
  scrapedAt: number;
  durationMs: number | null;
}

export interface ScrapingErrorsResponse {
  errors: ScrapingError[];
  total: number;
  limit: number;
  offset: number;
}

export interface ScrapingFacility {
  id: string;
  name: string;
  originName: string | null;
  lastScrapedAt: number | null;
  lastSuccess: number | null;
  lastDurationMs: number | null;
  lastError: string | null;
}

export interface ScrapingFacilitiesResponse {
  facilities: ScrapingFacility[];
  total: number;
  limit: number;
  offset: number;
}

export interface TriggerScrapeResponse {
  facilityId: string;
  facilityName: string;
  [key: string]: unknown;
}

export interface AvailabilityDayEvent {
  day: string;
  became_available: number;
  became_unavailable: number;
}

export interface AvailabilityEventsResponse {
  events: AvailabilityDayEvent[];
}

export interface HotFacility {
  facilityId: string;
  facilityName: string | null;
  eventCount: number;
}

export interface HotFacilitiesResponse {
  facilities: HotFacility[];
}

export interface AdminUser {
  owner: string;
  alertCount: number;
  channels: string;
  subscriptionStatus: string;
  lastActivity: number;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
  limit: number;
  offset: number;
}

export interface SystemHealth {
  rowCounts: {
    scrapeLog: number;
    availability: number;
    availabilityEvent: number;
  };
}

export const adminApi = {
  me: () => fetchAdmin<AdminMeResponse>('/me'),

  stats: () => fetchAdmin<AdminStats>('/stats'),

  scraping: {
    summary: () => fetchAdmin<ScrapingSummary>('/scraping/summary'),

    errors: (params?: { limit?: number; offset?: number }) => {
      const query = new URLSearchParams();
      if (params?.limit !== undefined) query.set('limit', String(params.limit));
      if (params?.offset !== undefined) query.set('offset', String(params.offset));
      const qs = query.toString();
      return fetchAdmin<ScrapingErrorsResponse>(`/scraping/errors${qs ? `?${qs}` : ''}`);
    },

    facilities: (params?: { limit?: number; offset?: number; provider?: string; status?: string; sort?: string }) => {
      const query = new URLSearchParams();
      if (params?.limit !== undefined) query.set('limit', String(params.limit));
      if (params?.offset !== undefined) query.set('offset', String(params.offset));
      if (params?.provider) query.set('provider', params.provider);
      if (params?.status) query.set('status', params.status);
      if (params?.sort) query.set('sort', params.sort);
      const qs = query.toString();
      return fetchAdmin<ScrapingFacilitiesResponse>(`/scraping/facilities${qs ? `?${qs}` : ''}`);
    },

    triggerScrape: (facilityId: string) =>
      fetchAdmin<TriggerScrapeResponse>(`/scraping/facilities/${facilityId}/scrape`, { method: 'POST' }),
  },

  availability: {
    events: () => fetchAdmin<AvailabilityEventsResponse>('/availability/events'),
    hotFacilities: () => fetchAdmin<HotFacilitiesResponse>('/availability/hot-facilities'),
  },

  users: (params?: { limit?: number; offset?: number; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query.set('offset', String(params.offset));
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return fetchAdmin<AdminUsersResponse>(`/users${qs ? `?${qs}` : ''}`);
  },

  system: {
    health: () => fetchAdmin<SystemHealth>('/system/health'),
  },
};
